import invariant from 'invariant';
import isFunction from 'lodash/isFunction';
import { Action } from 'redux';
import { CallEffect, cancelled, put } from 'redux-saga/effects';

import { CommonApiResponseType } from 'src/models/BaseApi';
import { convertRawApiErrorResponseToPreparedApiErrors } from 'src/utils/errors';
import { isHttpResponseValid } from 'src/utils/httpResponse';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { SafeAnyType } from 'src/utils/safeAny';

/**
 * use this saga when you need to check a response is it valid or not
 *
 * runs logicFunction
 * then checks if a response is valid
 * then if valid, runs successFunction or if not - errorFunction
 */
export function* httpResponseCheckSaga<T extends CommonApiResponseType>(
  logicFunction: () => Generator<CallEffect<T>>,
  successFunction: (response: T) => void,
  errorFunction: (e: CommonApiResponseType, response?: T) => Action | Generator,
  finallyFunction?: () => void
): Generator {
  const errorFunctionType = errorFunction.constructor.name;

  try {
    const response: CommonApiResponseType = yield logicFunction();

    invariant(!(response === null || response === undefined), 'logicFunction must return a value');

    if (isHttpResponseValid(response)) {
      yield successFunction(response);
    } else if (errorFunctionType === 'Function') {
      yield put(errorFunction(convertRawApiErrorResponseToPreparedApiErrors(response), response) as Action);
    } else if (errorFunctionType === 'GeneratorFunction' || typeof errorFunction === 'function') {
      yield errorFunction(convertRawApiErrorResponseToPreparedApiErrors(response), response);
    }
  } catch (e: SafeAnyType) {
    // eslint-disable-next-line no-console
    console.error(e);

    if (errorFunctionType === 'Function') {
      yield put(errorFunction(e.toString()) as Action);
    }
    if (errorFunctionType === 'GeneratorFunction' || typeof errorFunction === 'function') {
      yield errorFunction(e.toString());
    }
  } finally {
    if (yield cancelled()) {
      yield put({ type: 'common saga canceled' });
    }
    if (isFunction(finallyFunction)) {
      yield finallyFunction();
    }
  }
}
